import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, Copy, Eraser } from "lucide-react";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  solveStandard,
  vertexToStandard,
  factoredToStandard,
  standardFormTex,
  sampleParabola,
  autoXRange,
  type QuadraticResult,
} from "@/lib/calculators/quadratic";

type Bilingual = { en: string; bn: string };
type FormKind = "standard" | "vertex" | "factored";

const STR = {
  h1: { en: "Quadratic Formula Solver", bn: "কোয়াড্রাটিক ফর্মুলা" },
  desc: {
    en: "Solve any quadratic equation ax² + bx + c = 0 with discriminant analysis, real or complex roots, vertex, factored form and a graph.",
    bn: "যেকোনো দ্বিঘাত সমীকরণ ax² + bx + c = 0 — নিরূপক বিশ্লেষণ, বাস্তব বা জটিল মূল, শীর্ষবিন্দু, উৎপাদক রূপ ও গ্রাফসহ।",
  },
  form: { en: "Equation form", bn: "সমীকরণের রূপ" },
  standard: { en: "Standard ax² + bx + c", bn: "প্রমিত ax² + bx + c" },
  vertex: { en: "Vertex a(x − h)² + k", bn: "শীর্ষ a(x − h)² + k" },
  factored: { en: "Factored a(x − p)(x − q)", bn: "উৎপাদক a(x − p)(x − q)" },
  solve: { en: "Solve", bn: "সমাধান করুন" },
  clear: { en: "Clear", bn: "মুছুন" },
  copy: { en: "Copy", bn: "কপি" },
  result: { en: "Solution", bn: "সমাধান" },
  steps: { en: "Steps", bn: "ধাপসমূহ" },
  showSteps: { en: "Show steps", bn: "ধাপ দেখান" },
  showGraph: { en: "Show graph", bn: "গ্রাফ দেখান" },
  discriminant: { en: "Discriminant", bn: "নিরূপক" },
  roots: { en: "Roots", bn: "মূল" },
  vertexPoint: { en: "Vertex", bn: "শীর্ষবিন্দু" },
  axis: { en: "Axis of symmetry", bn: "প্রতিসাম্য অক্ষ" },
  yIntercept: { en: "Y-intercept", bn: "Y অক্ষ ছেদ" },
  vertexFormLabel: { en: "Vertex form", bn: "শীর্ষ রূপ" },
  factoredFormLabel: { en: "Factored form", bn: "উৎপাদক রূপ" },
  twoReal: { en: "Two distinct real roots (Δ > 0)", bn: "দুটি স্বতন্ত্র বাস্তব মূল (Δ > 0)" },
  repeated: { en: "One repeated real root (Δ = 0)", bn: "একটি পুনরাবৃত্ত বাস্তব মূল (Δ = 0)" },
  complex: { en: "No real roots — two complex conjugates (Δ < 0)", bn: "বাস্তব মূল নেই — দুটি জটিল অনুবন্ধী (Δ < 0)" },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How to solve a quadratic equation?", bn: "দ্বিঘাত সমীকরণ কীভাবে সমাধান করব?" },
    a: {
      en: "Write it as ax² + bx + c = 0, then apply the quadratic formula x = (−b ± √(b²−4ac)) / 2a. The discriminant b² − 4ac tells you the nature of the roots before you compute them.",
      bn: "সমীকরণটি ax² + bx + c = 0 আকারে লিখুন, এরপর সূত্র প্রয়োগ করুন: x = (−b ± √(b²−4ac)) / 2a। নিরূপক b² − 4ac থেকে মূলের প্রকৃতি আগেই বোঝা যায়।",
    },
  },
  {
    q: { en: "What is the discriminant?", bn: "নিরূপক কী?" },
    a: {
      en: "The discriminant Δ = b² − 4ac is the part inside the square root. If Δ > 0 there are two distinct real roots, if Δ = 0 one repeated real root, if Δ < 0 two complex conjugate roots.",
      bn: "নিরূপক Δ = b² − 4ac — সূত্রের বর্গমূলের ভেতরের অংশ। Δ > 0 হলে দুটি ভিন্ন বাস্তব মূল, Δ = 0 হলে একটি পুনরাবৃত্ত বাস্তব মূল, Δ < 0 হলে দুটি জটিল অনুবন্ধী মূল।",
    },
  },
  {
    q: { en: "How is the quadratic formula derived?", bn: "কোয়াড্রাটিক সূত্র কীভাবে প্রতিপাদন করা হয়?" },
    a: {
      en: "Start with ax² + bx + c = 0. Divide by a, complete the square: (x + b/2a)² = (b² − 4ac) / 4a². Take square roots and isolate x to get x = (−b ± √(b²−4ac)) / 2a.",
      bn: "ax² + bx + c = 0 দিয়ে শুরু করে a দ্বারা ভাগ করুন, এরপর বর্গ পূরণ করুন: (x + b/2a)² = (b² − 4ac) / 4a²। বর্গমূল নিয়ে x বের করলেই x = (−b ± √(b²−4ac)) / 2a।",
    },
  },
  {
    q: { en: "What if a = 0?", bn: "a = 0 হলে কী হয়?" },
    a: {
      en: "Then it's not a quadratic — it reduces to a linear equation bx + c = 0 with x = −c/b (provided b ≠ 0). The calculator will warn you instead of dividing by zero.",
      bn: "তখন এটি দ্বিঘাত নয় — রৈখিক সমীকরণে রূপ নেয়: bx + c = 0, যার মূল x = −c/b (যদি b ≠ 0)। ক্যালকুলেটর ভাগ-শূন্য না করে সতর্ক করবে।",
    },
  },
  {
    q: { en: "What is the vertex form?", bn: "শীর্ষ রূপ কী?" },
    a: {
      en: "Vertex form a(x − h)² + k makes the parabola's vertex (h, k) explicit. We compute h = −b/(2a) and k = c − b²/(4a). Useful for graphing and optimisation.",
      bn: "শীর্ষ রূপ a(x − h)² + k এ প্যারাবোলার শীর্ষবিন্দু (h, k) সরাসরি দেখা যায়। h = −b/(2a), k = c − b²/(4a)। গ্রাফ আঁকা ও সর্বোচ্চ/সর্বনিম্ন বের করতে কাজে লাগে।",
    },
  },
  {
    q: { en: "Are complex roots important?", bn: "জটিল মূল কেন গুরুত্বপূর্ণ?" },
    a: {
      en: "Yes — they appear whenever the parabola does not cross the x-axis. In HSC and engineering, complex roots show up in oscillation, AC circuits and signal analysis.",
      bn: "হ্যাঁ — যখন প্যারাবোলা x অক্ষ স্পর্শ করে না, তখনই জটিল মূল আসে। HSC ও প্রকৌশলে দোলন, AC সার্কিট ও সিগন্যাল বিশ্লেষণে এগুলো ব্যবহৃত হয়।",
    },
  },
  {
    q: { en: "Vieta's formulas?", bn: "ভিয়েটার সূত্র?" },
    a: {
      en: "For ax² + bx + c = 0 with roots r₁, r₂: sum r₁ + r₂ = −b/a and product r₁·r₂ = c/a. These give a quick sanity check on your answer.",
      bn: "ax² + bx + c = 0 এর মূল r₁, r₂ হলে: যোগফল r₁ + r₂ = −b/a, গুণফল r₁·r₂ = c/a। উত্তর যাচাইয়ের দ্রুত উপায়।",
    },
  },
  {
    q: { en: "কোয়াড্রাটিক সমীকরণ কীভাবে সমাধান করব?", bn: "কোয়াড্রাটিক সমীকরণ কীভাবে সমাধান করব?" },
    a: {
      en: "সমীকরণটি ax² + bx + c = 0 আকারে আনুন, a, b, c সহগ চিহ্নিত করুন, তারপর x = (−b ± √(b² − 4ac)) / 2a সূত্র প্রয়োগ করুন। প্রথমে নিরূপক হিসাব করে মূলের প্রকৃতি বুঝে নিন।",
      bn: "সমীকরণটি ax² + bx + c = 0 আকারে আনুন, a, b, c সহগ চিহ্নিত করুন, তারপর x = (−b ± √(b² − 4ac)) / 2a সূত্র প্রয়োগ করুন। প্রথমে নিরূপক হিসাব করে মূলের প্রকৃতি বুঝে নিন।",
    },
  },
];

export const Route = createFileRoute("/quadratic-formula-solver")({
  head: () => {
    const seo = buildSeo({
      title: "Quadratic Formula Solver — Step by Step | Bangla Calculator",
      description:
        "Free quadratic formula solver. Solve ax²+bx+c=0 with steps, discriminant, roots, vertex, graph. For SSC/HSC math. কোয়াড্রাটিক ফর্মুলা ক্যালকুলেটর।",
      path: "/quadratic-formula-solver",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Quadratic Formula Solver", path: "/quadratic-formula-solver" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: QuadraticFormulaPage,
});

const num = (s: string, fallback = NaN) => {
  const v = Number(s);
  return Number.isFinite(v) ? v : fallback;
};

function QuadraticFormulaPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];

  const [formKind, setFormKind] = useState<FormKind>("standard");
  // Standard
  const [aStr, setAStr] = useState("1");
  const [bStr, setBStr] = useState("-5");
  const [cStr, setCStr] = useState("6");
  // Vertex
  const [vaStr, setVaStr] = useState("1");
  const [hStr, setHStr] = useState("2");
  const [kStr, setKStr] = useState("-1");
  // Factored
  const [faStr, setFaStr] = useState("1");
  const [pStr, setPStr] = useState("2");
  const [qStr, setQStr] = useState("3");
  const [showSteps, setShowSteps] = useState(true);
  const [showGraph, setShowGraph] = useState(true);

  const coeffs = useMemo(() => {
    if (formKind === "standard") {
      return { a: num(aStr), b: num(bStr), c: num(cStr) };
    }
    if (formKind === "vertex") {
      return vertexToStandard(num(vaStr), num(hStr), num(kStr));
    }
    return factoredToStandard(num(faStr), num(pStr), num(qStr));
  }, [formKind, aStr, bStr, cStr, vaStr, hStr, kStr, faStr, pStr, qStr]);

  const result: QuadraticResult = useMemo(
    () => solveStandard(coeffs.a, coeffs.b, coeffs.c),
    [coeffs.a, coeffs.b, coeffs.c],
  );

  const equationTex = useMemo(() => {
    if (!Number.isFinite(coeffs.a) || !Number.isFinite(coeffs.b) || !Number.isFinite(coeffs.c)) return "";
    return standardFormTex(coeffs.a, coeffs.b, coeffs.c);
  }, [coeffs]);

  const onClear = () => {
    if (formKind === "standard") { setAStr(""); setBStr(""); setCStr(""); }
    else if (formKind === "vertex") { setVaStr(""); setHStr(""); setKStr(""); }
    else { setFaStr(""); setPStr(""); setQStr(""); }
  };

  const copyResult = () => {
    if (!result.ok || typeof navigator === "undefined") return;
    const lines = [
      `Equation: ${standardFormTex(result.a, result.b, result.c)}`,
      `Discriminant Δ = ${result.discriminant}`,
      `Roots: ${result.rootsTex.join("; ")}`,
      `Vertex: (${result.vertex.h}, ${result.vertex.k})`,
    ];
    navigator.clipboard?.writeText(lines.join("\n"));
  };

  const KindBadge = result.ok && (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        result.kind === "two-real" && "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        result.kind === "repeated" && "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400",
        result.kind === "complex" && "border-purple-500/40 bg-purple-500/10 text-purple-700 dark:text-purple-400",
        lang === "bn" && "font-bn",
      )}
    >
      {result.kind === "two-real" ? tr("twoReal") : result.kind === "repeated" ? tr("repeated") : tr("complex")}
    </span>
  );

  return (
    <CalculatorLayout
      slug="quadratic-formula-solver"
      title={tr("h1")}
      description={tr("desc")}
      iconName="SquareFunction"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Form selector */}
      <div className="space-y-1.5">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("form")}</label>
        <div className="flex flex-wrap gap-1.5">
          {([
            { v: "standard", label: tr("standard") },
            { v: "vertex", label: tr("vertex") },
            { v: "factored", label: tr("factored") },
          ] as { v: FormKind; label: string }[]).map((opt) => {
            const active = formKind === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => setFormKind(opt.v)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition",
                  active ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card text-muted-foreground hover:bg-muted",
                  lang === "bn" && "font-bn",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Coefficient inputs */}
      <section className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {formKind === "standard" && (
          <>
            <CoefInput label="a" value={aStr} onChange={setAStr} />
            <CoefInput label="b" value={bStr} onChange={setBStr} />
            <CoefInput label="c" value={cStr} onChange={setCStr} />
          </>
        )}
        {formKind === "vertex" && (
          <>
            <CoefInput label="a" value={vaStr} onChange={setVaStr} />
            <CoefInput label="h" value={hStr} onChange={setHStr} />
            <CoefInput label="k" value={kStr} onChange={setKStr} />
          </>
        )}
        {formKind === "factored" && (
          <>
            <CoefInput label="a" value={faStr} onChange={setFaStr} />
            <CoefInput label="p" value={pStr} onChange={setPStr} />
            <CoefInput label="q" value={qStr} onChange={setQStr} />
          </>
        )}
      </section>

      {/* Live equation preview */}
      {equationTex && (
        <section className="mt-4 rounded-xl border border-border bg-card/40 p-3">
          <div className={cn("mb-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn" ? "সমীকরণ" : "Equation"}
          </div>
          <div className="overflow-x-auto">
            <BlockMath math={equationTex} />
          </div>
        </section>
      )}

      {/* Action row */}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" onClick={onClear}>
          <Eraser size={14} className="mr-1.5" /> {tr("clear")}
        </Button>
        <label className="ml-auto flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={showSteps} onChange={(e) => setShowSteps(e.target.checked)} className="accent-accent" />
          <span className={lang === "bn" ? "font-bn" : ""}>{tr("showSteps")}</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={showGraph} onChange={(e) => setShowGraph(e.target.checked)} className="accent-accent" />
          <span className={lang === "bn" ? "font-bn" : ""}>{tr("showGraph")}</span>
        </label>
      </div>

      {/* Result */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-5 rounded-xl border border-border bg-card p-4"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("result")}</h3>
          {result.ok && (
            <Button variant="ghost" size="sm" onClick={copyResult}>
              <Copy size={12} className="mr-1" /> {tr("copy")}
            </Button>
          )}
        </div>

        {!result.ok && (
          <div className={cn(
            "flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <p>{result.error?.[lang]}</p>
          </div>
        )}

        {result.ok && (
          <div className="space-y-3">
            {KindBadge}

            {/* Roots highlight */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {result.rootsTex.map((rt, i) => (
                <div key={i} className="rounded-lg border border-accent/40 bg-accent/5 p-3">
                  <div className={cn("mb-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                    {result.kind === "repeated" ? `x` : `x_{${i + 1}}`}
                  </div>
                  <div className="overflow-x-auto">
                    <InlineMath math={`x${result.kind === "repeated" ? "" : `_{${i + 1}}`} = ${rt}`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Discriminant + extras */}
            <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <Fact label={tr("discriminant")} value={fmt(result.discriminant)} mono />
              <Fact label={tr("vertexPoint")} value={`(${fmt(result.vertex.h)}, ${fmt(result.vertex.k)})`} mono />
              <Fact label={tr("axis")} value={`x = ${fmt(result.axisOfSymmetry)}`} mono />
              <Fact label={tr("yIntercept")} value={fmt(result.yIntercept)} mono />
            </dl>

            {/* Equivalent forms */}
            <div className="space-y-2 rounded-lg border border-border bg-background/50 p-3 text-sm">
              <div>
                <div className={cn("mb-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("vertexFormLabel")}</div>
                <div className="overflow-x-auto"><InlineMath math={result.vertexFormTex} /></div>
              </div>
              {result.factoredFormTex && (
                <div>
                  <div className={cn("mb-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("factoredFormLabel")}</div>
                  <div className="overflow-x-auto"><InlineMath math={result.factoredFormTex} /></div>
                </div>
              )}
            </div>

            {/* Graph */}
            {showGraph && (
              <ParabolaPlot result={result} />
            )}

            {/* Steps */}
            {showSteps && result.steps.length > 0 && (
              <div>
                <div className={cn("mb-1.5 text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{tr("steps")}</div>
                <ol className="space-y-2">
                  {result.steps.map((s, i) => (
                    <li key={i} className="rounded-md border border-border bg-background p-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[11px] font-semibold text-accent">{i + 1}</span>
                        <p className={cn("min-w-0 flex-1", lang === "bn" && "font-bn")}>{s[lang]}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </motion.section>

      {/* Educational content */}
      <article className={cn("mt-8 space-y-5 text-sm leading-relaxed", lang === "bn" && "font-bn")}>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "দ্বিঘাত সমীকরণ কী?" : "What is a quadratic equation?"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "একটি দ্বিঘাত সমীকরণ হলো এমন একটি বহুপদী সমীকরণ যার সর্বোচ্চ ঘাত ২। প্রমিত রূপ ax² + bx + c = 0, যেখানে a ≠ 0। এর গ্রাফ একটি প্যারাবোলা এবং সমাধান হলো প্যারাবোলাটি যেখানে x অক্ষ ছেদ করে — যদি ছেদ করে।"
              : "A quadratic equation is a polynomial equation of degree 2. Its standard form is ax² + bx + c = 0 with a ≠ 0. The graph is a parabola and the real solutions are the points where the parabola crosses the x-axis (when it does)."}
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "কোয়াড্রাটিক সূত্র" : "The quadratic formula"}</h2>
          <BlockMath math={"x = \\dfrac{-b \\pm \\sqrt{b^{2} - 4ac}}{2a}"} />
          <p className="mt-1 text-muted-foreground">
            {lang === "bn"
              ? "যেকোনো ax² + bx + c = 0 (a ≠ 0) সমীকরণের মূল এই সূত্র দিয়ে বের করা যায়। ± চিহ্ন দুটি মূল দেয়।"
              : "This solves any ax² + bx + c = 0 with a ≠ 0. The ± sign produces the two roots."}
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "নিরূপক — মূলের প্রকৃতি" : "Discriminant — nature of roots"}</h2>
          <ul className="ml-5 list-disc space-y-1 text-muted-foreground">
            <li>{lang === "bn" ? "Δ > 0 → দুটি স্বতন্ত্র বাস্তব মূল; প্যারাবোলা x অক্ষকে দুটি বিন্দুতে ছেদ করে।" : "Δ > 0 → two distinct real roots; the parabola crosses the x-axis at two points."}</li>
            <li>{lang === "bn" ? "Δ = 0 → একটি পুনরাবৃত্ত বাস্তব মূল; প্যারাবোলা x অক্ষ স্পর্শ করে।" : "Δ = 0 → one repeated real root; the parabola is tangent to the x-axis."}</li>
            <li>{lang === "bn" ? "Δ < 0 → বাস্তব মূল নেই; দুটি জটিল অনুবন্ধী মূল।" : "Δ < 0 → no real roots; two complex conjugate roots."}</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "ধাপে ধাপে উদাহরণ" : "Step-by-step example"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn" ? "x² − 5x + 6 = 0 সমাধান:" : "Solve x² − 5x + 6 = 0:"}
          </p>
          <ol className="ml-5 mt-1 list-decimal space-y-1 text-muted-foreground">
            <li>a = 1, b = −5, c = 6</li>
            <li>Δ = (−5)² − 4·1·6 = 25 − 24 = 1</li>
            <li>x = (5 ± √1) / 2 = (5 ± 1) / 2</li>
            <li>x₁ = 3, x₂ = 2</li>
            <li>{lang === "bn" ? "যাচাই: ভিয়েটার সূত্র — যোগফল 5 = −b/a, গুণফল 6 = c/a ✓" : "Check with Vieta: sum 5 = −b/a, product 6 = c/a ✓"}</li>
          </ol>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "শীর্ষ রূপ ও প্যারাবোলা" : "Vertex form and the parabola"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "ax² + bx + c কে a(x − h)² + k আকারে লিখলে শীর্ষবিন্দু (h, k) সরাসরি পাওয়া যায়, যেখানে h = −b/(2a), k = c − b²/(4a)। a > 0 হলে প্যারাবোলা উপরমুখী (k = ন্যূনতম মান), a < 0 হলে নিম্নমুখী (k = সর্বোচ্চ মান)।"
              : "Rewriting ax² + bx + c as a(x − h)² + k exposes the vertex (h, k) where h = −b/(2a) and k = c − b²/(4a). When a > 0 the parabola opens upward and k is the minimum; when a < 0 it opens downward and k is the maximum."}
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "উৎপাদক রূপ" : "Factored form"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "যদি বাস্তব মূল r₁, r₂ থাকে, সমীকরণটি a(x − r₁)(x − r₂) = 0 আকারে লেখা যায়। মূল মুখে দেখা গেলে এ পদ্ধতি দ্রুত কাজে আসে।"
              : "When real roots r₁, r₂ exist, the equation factors as a(x − r₁)(x − r₂) = 0. Spotting roots by inspection (small integer coefficients) is the fastest path."}
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "SSC শিক্ষার্থীদের জন্য" : "For SSC students"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "বোর্ডে দ্বিঘাত সমীকরণের মূল নির্ণয়, শব্দ-সমস্যা (গতি, ক্ষেত্রফল) ও মূলের প্রকৃতি ব্যাখ্যার প্রশ্ন আসে। নিজে কাগজে সমাধান করে এখানে যাচাই করুন — ধাপগুলো মিলিয়ে দেখলে ভুল কোথায় হয়েছে বোঝা যায়।"
              : "Board questions cover finding roots, word problems (motion, area), and explaining the nature of roots. Solve on paper first, then verify here — comparing each step pinpoints where you went wrong."}
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "HSC শিক্ষার্থীদের জন্য" : "For HSC students"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "HSC গণিতে দ্বিঘাত আরও গভীর — ভিয়েটার সূত্র, মূলের সম্পর্ক থেকে নতুন সমীকরণ গঠন, জটিল মূল ও বহুপদীতে প্রয়োগ। উপরের ধাপগুলো এই বিষয়ের ভিত্তি গড়ে দেয়।"
              : "HSC takes quadratics further: Vieta's formulas, building new equations from given root relations, complex roots and links to higher-degree polynomials. The steps above lay the foundation for those topics."}
          </p>
        </section>
      </article>

      {/* Related */}
      <section className="mt-8">
        <h2 className={cn("mb-3 text-base font-bold", lang === "bn" && "font-bn")}>
          {lang === "bn" ? "সম্পর্কিত ক্যালকুলেটর" : "Related calculators"}
        </h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to="/math-solver" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "ম্যাথ সলভার" : "Math Solver"}
          </Link>
          <Link to="/scientific-calculator" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "সায়েন্টিফিক ক্যালকুলেটর" : "Scientific Calculator"}
          </Link>
          <Link to="/graphing-calculator" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "গ্রাফিং ক্যালকুলেটর" : "Graphing Calculator"}
          </Link>
          <Link to="/integral-calculator" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "ইন্টিগ্রাল ক্যালকুলেটর" : "Integral Calculator"}
          </Link>
        </div>
      </section>
    </CalculatorLayout>
  );
}

function CoefInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      <input
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-border bg-card px-3 font-mono text-sm outline-none focus:ring-2 focus:ring-accent/60"
      />
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-background p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-sm font-semibold", mono && "font-mono")}>{value}</div>
    </div>
  );
}

function fmt(n: number, digits = 4): string {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n - Math.round(n)) < 1e-10) return Math.round(n).toString();
  return Number(n.toFixed(digits)).toString();
}

// ---------------------------------------------------------------------------
// Parabola plot — pure canvas, mirrors graphing-calculator approach
// ---------------------------------------------------------------------------
function ParabolaPlot({ result }: { result: QuadraticResult }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 600, h: 360 });

  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.max(280, entry.contentRect.width);
        setSize({ w: Math.round(w), h: Math.round(Math.min(420, w * 0.62)) });
      }
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    cvs.width = Math.floor(size.w * dpr);
    cvs.height = Math.floor(size.h * dpr);
    cvs.style.width = `${size.w}px`;
    cvs.style.height = `${size.h}px`;
    const ctx = cvs.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawParabola(ctx, size.w, size.h, result);
  }, [size, result]);

  return (
    <div ref={wrapperRef} className="overflow-hidden rounded-xl border border-border bg-white dark:bg-zinc-950">
      <canvas ref={canvasRef} className="block w-full" />
    </div>
  );
}

function drawParabola(ctx: CanvasRenderingContext2D, w: number, h: number, result: QuadraticResult) {
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  ctx.fillStyle = isDark ? "#09090b" : "#ffffff";
  ctx.fillRect(0, 0, w, h);

  const { xMin, xMax } = autoXRange(result);
  const pts = sampleParabola(result.a, result.b, result.c, xMin, xMax, 220);

  // Y range — frame vertex + a margin
  let yLo = Math.min(result.vertex.k, ...pts.map((p) => p.y));
  let yHi = Math.max(result.vertex.k, ...pts.map((p) => p.y));
  // Always include y=0 so x-axis is visible
  yLo = Math.min(yLo, 0);
  yHi = Math.max(yHi, 0);
  if (yHi - yLo < 1e-9) { yLo -= 1; yHi += 1; }
  const padY = (yHi - yLo) * 0.15;
  yLo -= padY; yHi += padY;

  const xToPx = (x: number) => ((x - xMin) / (xMax - xMin)) * w;
  const yToPx = (y: number) => h - ((y - yLo) / (yHi - yLo)) * h;

  const grid = isDark ? "#1f1f23" : "#f1f5f9";
  const axis = isDark ? "#71717a" : "#52525b";
  const text = isDark ? "#a1a1aa" : "#52525b";

  // grid (every 1 unit roughly)
  const xStep = niceStep(xMax - xMin);
  const yStep = niceStep(yHi - yLo);
  ctx.strokeStyle = grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = Math.ceil(xMin / xStep) * xStep; x <= xMax; x += xStep) {
    const px = xToPx(x); ctx.moveTo(px, 0); ctx.lineTo(px, h);
  }
  for (let y = Math.ceil(yLo / yStep) * yStep; y <= yHi; y += yStep) {
    const py = yToPx(y); ctx.moveTo(0, py); ctx.lineTo(w, py);
  }
  ctx.stroke();

  // axes
  ctx.strokeStyle = axis;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  if (yLo <= 0 && yHi >= 0) { const py = yToPx(0); ctx.moveTo(0, py); ctx.lineTo(w, py); }
  if (xMin <= 0 && xMax >= 0) { const px = xToPx(0); ctx.moveTo(px, 0); ctx.lineTo(px, h); }
  ctx.stroke();

  // axis labels
  ctx.fillStyle = text;
  ctx.font = "11px ui-sans-serif, system-ui";
  ctx.textBaseline = "top";
  for (let x = Math.ceil(xMin / xStep) * xStep; x <= xMax; x += xStep) {
    if (Math.abs(x) < xStep / 2) continue;
    const px = xToPx(x);
    const py = Math.min(h - 14, Math.max(2, yToPx(0) + 2));
    ctx.fillText(formatTick(x, xStep), px + 2, py);
  }
  ctx.textBaseline = "middle";
  for (let y = Math.ceil(yLo / yStep) * yStep; y <= yHi; y += yStep) {
    if (Math.abs(y) < yStep / 2) continue;
    const py = yToPx(y);
    const px = Math.min(w - 24, Math.max(2, xToPx(0) + 4));
    ctx.fillText(formatTick(y, yStep), px, py);
  }

  // axis of symmetry — dashed
  ctx.strokeStyle = isDark ? "#64748b" : "#94a3b8";
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  const axisX = xToPx(result.vertex.h);
  ctx.moveTo(axisX, 0); ctx.lineTo(axisX, h);
  ctx.stroke();
  ctx.setLineDash([]);

  // parabola
  ctx.strokeStyle = "#2563eb";
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  let started = false;
  for (const p of pts) {
    const py = yToPx(p.y);
    if (py < -2000 || py > h + 2000) {
      if (started) { ctx.stroke(); ctx.beginPath(); started = false; }
      continue;
    }
    const px = xToPx(p.x);
    if (!started) { ctx.moveTo(px, py); started = true; } else { ctx.lineTo(px, py); }
  }
  if (started) ctx.stroke();

  // roots (real)
  ctx.fillStyle = "#dc2626";
  ctx.strokeStyle = isDark ? "#09090b" : "#ffffff";
  ctx.lineWidth = 2;
  for (const r of result.roots) {
    if (r.im !== 0) continue;
    const px = xToPx(r.re), py = yToPx(0);
    ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = text;
    ctx.font = "11px ui-sans-serif, system-ui";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(`x = ${formatTick(r.re, xStep)}`, px + 6, py - 6);
    ctx.fillStyle = "#dc2626";
  }

  // vertex
  ctx.fillStyle = "#16a34a";
  const vx = xToPx(result.vertex.h), vy = yToPx(result.vertex.k);
  ctx.beginPath(); ctx.arc(vx, vy, 5, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = isDark ? "#09090b" : "#ffffff";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = text;
  ctx.font = "11px ui-sans-serif, system-ui";
  ctx.fillText(
    `(${formatTick(result.vertex.h, xStep)}, ${formatTick(result.vertex.k, yStep)})`,
    vx + 6,
    vy + (result.a > 0 ? 14 : -6),
  );
}

function niceStep(range: number): number {
  if (!Number.isFinite(range) || range <= 0) return 1;
  const target = range / 8;
  const exp = Math.floor(Math.log10(target));
  const base = Math.pow(10, exp);
  const mult = target / base;
  let m = 1;
  if (mult >= 5) m = 5;
  else if (mult >= 2) m = 2;
  return m * base;
}

function formatTick(v: number, step: number): string {
  const decimals = step >= 1 ? 0 : Math.max(0, -Math.floor(Math.log10(step)));
  return Number(v.toFixed(decimals)).toString();
}
